import { computed, makeObservable, observable } from 'mobx'
import { once } from 'lodash'
import {
  ProviderDTO,
  ProviderMembershipDTO,
  ProviderStatusDTO,
  ProviderTypeDTO,
} from 'iam/dtos/provider'
import { Model } from 'infra/model'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { generateAvatarFallback } from 'utils/image-util'
import { ProviderActivePtinStatusDto } from './ProviderActivePtinStatusDto'

export interface ProviderMembership {
  workspaceId: string
  jobCapacity: number
}

export enum ProviderStatus {
  Active = 'ACTIVE',
  Decommissioned = 'DECOMMISSIONED',
  Suspended = 'SUSPENDED',
  OnLeave = 'ON_LEAVE',
  Null = 'NULL',
}

export enum ProviderPtinActiveStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  NotApplicable = 'NOT_APPLICABLE',
}

export enum ProviderType {
  CPA = 'CPA',
  EA = 'EA',
  NotApplicable = 'NOT_APPLICABLE',
}

export default class Provider extends Model<ProviderDTO, Provider> {
  userPublicId: Id = Id.none
  givenName: string = ''
  familyName: string = ''
  email: string = ''
  phone: string = ''
  picture: string = ''
  workspaces: ProviderMembership[] = []
  globalJobCapacity: number = 50
  softwareExperience: string[] = []
  softwareOwned: string[] = []
  supplementalSoftwareExperience: string[] = []
  yearsExperience: number = 0
  stateRegion: string = ''
  activePtin = ProviderPtinActiveStatus.NotApplicable
  providerStatus: ProviderStatus = ProviderStatus.Null
  proType: ProviderType = ProviderType.NotApplicable
  proManager: string = ''

  specialties: string[] = []
  dateCreated: Date = DateZero()
  dateModified: Date = DateZero()

  /**
   * Default value to use for provider.
   */
  static get none() {
    return lazyNone()
  }

  constructor() {
    super()
    makeObservable(this, {
      userPublicId: observable,
      givenName: observable,
      familyName: observable,
      email: observable,
      phone: observable,
      picture: observable,
      workspaces: observable,
      globalJobCapacity: observable,
      softwareExperience: observable,
      softwareOwned: observable,
      supplementalSoftwareExperience: observable,
      yearsExperience: observable,
      stateRegion: observable,
      activePtin: observable,
      providerStatus: observable,
      specialties: observable,
      dateCreated: observable,
      dateModified: observable,
      proManager: observable,
      proType: observable,
      displayName: computed,
      avatar: computed,
      proTypeTitle: computed,
    })
  }

  /**
   * Returns the given and family names together.
   */
  get displayName() {
    return `${this.givenName} ${this.familyName}`.trim()
  }

  get proTypeTitle() {
    switch (this.proType) {
      case ProviderType.CPA:
        return 'CPA'
      case ProviderType.EA:
        return 'EA'
      default:
        return 'Unknown'
    }
  }

  get proPtinStatus() {
    switch (this.activePtin) {
      case ProviderPtinActiveStatus.Active:
        return 'Active'
      case ProviderPtinActiveStatus.Inactive:
      default:
        return 'Inactive'
    }
  }

  /**
   * Returns either the user-determined picture or a placeholder.
   */
  get avatar() {
    return (
      this.picture || generateAvatarFallback(this.displayName || this.email)
    )
  }

  parse(json: Partial<ProviderDTO>): Partial<Provider> {
    return {
      userPublicId: json.user_public_id,
      givenName: json.given_name,
      familyName: json.family_name,
      email: json.email,
      phone: json.phone,
      picture: json.picture,
      workspaces:
        json.workspaces && parseProviderMembershipDto(json.workspaces),
      globalJobCapacity: json.global_job_capacity,
      softwareExperience: json.software_experience,
      softwareOwned: json.software_owned,
      supplementalSoftwareExperience: json.supplemental_software_experience,
      yearsExperience: json.years_experience,
      stateRegion: json.state_region,
      activePtin: parseProviderActivePtinDto(json.active_ptin),
      providerStatus:
        json.provider_status && parseProviderStatusDto(json.provider_status),
      specialties: json.specialties,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      proManager: json.pro_manager,
      proType: parseProviderTypeDto(json.pro_type),
    }
  }
}

function parseProviderTypeDto(dto?: ProviderTypeDTO): ProviderType {
  switch (dto) {
    case ProviderTypeDTO.CPA:
      return ProviderType.CPA
    case ProviderTypeDTO.EA:
      return ProviderType.EA
    default:
      return ProviderType.NotApplicable
  }
}

function parseProviderActivePtinDto(
  dto?: ProviderActivePtinStatusDto,
): ProviderPtinActiveStatus {
  switch (dto) {
    case ProviderActivePtinStatusDto.Active:
      return ProviderPtinActiveStatus.Active
    case ProviderActivePtinStatusDto.Inactive:
      return ProviderPtinActiveStatus.Inactive
    default:
      return ProviderPtinActiveStatus.NotApplicable
  }
}

function parseProviderMembershipDto(
  dto: ProviderMembershipDTO[],
): ProviderMembership[] {
  return dto.map((pm) => {
    return {
      workspaceId: pm.workspace_id,
      jobCapacity: pm.job_capacity,
    }
  })
}

function parseProviderStatusDto(dto: ProviderStatusDTO): ProviderStatus {
  switch (dto) {
    case ProviderStatusDTO.Active:
      return ProviderStatus.Active
    case ProviderStatusDTO.Decommissioned:
      return ProviderStatus.Decommissioned
    case ProviderStatusDTO.Suspended:
      return ProviderStatus.Suspended
    case ProviderStatusDTO.OnLeave:
      return ProviderStatus.OnLeave
    case ProviderStatusDTO.Null:
    default:
      return ProviderStatus.Null
  }
}

const lazyNone = once(() => new Provider())
